import { observer } from 'mobx-react-lite'

import { schema } from '@showrunner/prose-schemas'

import { type PageBreakScriptInfo } from '#choo-app/lib/editor/plugins/inlinePageBreaks'
import { PLUGIN_KEY as REVISION_PLUGIN_KEY } from '#choo-app/lib/editor/plugins/revision-asterisks/helpers'
import { useFeatureFlag, useMst } from '#hooks'
import { createComparisonEditorState } from '#util'
import { DEFAULT_READ_RATE } from '#util/constants'
import { FragmentDiff } from '#util/diffing/FragmentDiff'
import { NodeDiff } from '#util/diffing/NodeDiff'
import { getPageLayoutFromScript } from '#util/PageLayout'
import { ScriptSnapshotPayload } from '#util/ScriptoApiClient/types'

import { useDiffAlgorithm } from '../useDiffAlgorithm'
import {
  snapshotRevisionsLoader,
  useSnapshotData,
} from '../useSnapshotLandData'
import { SnapshotViewerBase } from '../viewer'

import { BodyPlaceholder } from './BodyPlaceholder'
import { IdenticalSnapshotsToast } from './IdenticalSnapshotsToast'

const PopulatedAsteriskView = observer(function PopulatedAsteriskView({
  newer,
  older,
  scriptName,
}: {
  newer: Pick<
    ScriptSnapshotPayload,
    'doc' | 'scriptFormat' | 'scriptId' | 'id' | 'revisions'
  >
  older: Pick<ScriptSnapshotPayload, 'doc'>
  scriptName?: string
}) {
  const mst = useMst()
  const [legacyPaginator] = useFeatureFlag('legacy-paginator')
  const useNewPaginator = !legacyPaginator
  const useRightSideMarks =
    (mst.user.prefs.snapshotRevisionMarkPlacement ?? 'right') === 'right'
  const highlightAdditions = mst.user.prefs.snapshotHighlightAdditions ?? true
  const showDeletions = mst.user.prefs.snapshotShowDeletions ?? false
  const diff = useDiffAlgorithm()

  const scriptInfo: PageBreakScriptInfo = {
    name: scriptName ? `${scriptName} - asterisk` : '',
    scriptType: newer.doc.attrs?.docType,
    readRate: mst.currentOrg?.readRate ?? DEFAULT_READ_RATE,
    scriptPrintPrefs: mst.user.scriptPrintPrefs,
  }

  const left = schema.nodeFromJSON(older.doc)
  const right = schema.nodeFromJSON(newer.doc)
  const comparison =
    diff.algorithm === 'changeset'
      ? new NodeDiff({ left, right })
      : new FragmentDiff({ left: left.content, right: right.content })

  const editorState = createComparisonEditorState({
    script: newer,
    comparison,
    useNewPaginator,
    scriptInfo,
    useRightSideMarks,
    highlightAdditions,
    showDeletions,
    loadActiveRevisionSnapshot: snapshotRevisionsLoader(mst.scrapi, newer),
  })

  const revisionPluginState = REVISION_PLUGIN_KEY.getState(editorState)

  // we intentionally ignore the current user pref. asterisks are only for pages
  const pageless = false

  const pageLayout = getPageLayoutFromScript(
    newer.doc,
    newer.scriptFormat.definition,
  )

  return (
    <SnapshotViewerBase
      pageless={pageless}
      editorZoom={mst.user.prefs.editorZoom}
      editorState={editorState}
      pageLayout={pageLayout}
    >
      {revisionPluginState?.identical && <IdenticalSnapshotsToast mb={25} />}
    </SnapshotViewerBase>
  )
})

// Once user has supplied the two IDs
const RequestedAsteriskView = observer(function RequestedAsteriskView({
  scriptId,
  scriptName,
  snap1Id,
  snap2Id,
}: {
  scriptId: string
  scriptName?: string
  snap1Id: string
  snap2Id: string
}) {
  const snap1query = useSnapshotData({ scriptId, snapshotId: snap1Id })
  const snap2query = useSnapshotData({ scriptId, snapshotId: snap2Id })
  if (snap1query.isPending || snap2query.isPending) {
    return <BodyPlaceholder variant="loading" />
  }
  if (snap1query.isError || snap2query.isError) {
    return <BodyPlaceholder variant="error" />
  }

  return (
    <PopulatedAsteriskView
      newer={snap1query.data}
      older={snap2query.data}
      scriptName={scriptName}
    />
  )
})

export const AsteriskView = observer(function AsteriskView({
  scriptId,
  scriptName,
}: {
  scriptId: string
  scriptName?: string
}) {
  const { snap1, snap2 } = useMst().view.snapshotLand

  if (snap1 && snap2) {
    return (
      <RequestedAsteriskView
        scriptId={scriptId}
        scriptName={scriptName}
        snap1Id={snap1}
        snap2Id={snap2}
      />
    )
  }

  const hasOne = snap1 || snap2

  return <BodyPlaceholder variant={hasOne ? 'pickAnother' : 'pickTwo'} />
})
